import React from 'react'
import get from 'lodash/get'
import { useRecordContext, sanitizeFieldRestProps } from 'react-admin'
import {
  Link,
  ListItem,
  Typography,
  styled,
  ListItemProps,
  ListItemTextProps,
  LinkProps,
  Box,
  Card,
  CardMedia,
  CardContent,
} from '@mui/material'
import { isBlob } from '../../utils/validators'
import { imageUrlConcat } from '../../utils/url-concat'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import { formatBytes } from '../../utils/file-size'
import { FileFieldProps, GenerateFileItemPropsFn, ListItemWithImageProps } from './type'
import isArray from 'lodash/isArray'
import isEmpty from 'lodash/isEmpty'

const Root = styled('div', {
  name: 'RaFileField',
  overridesResolver: (props, styles) => styles.root,
})({
  display: 'inline-block',
})

const ImageCaption = styled(Typography)(({ theme }) => ({
  textAlign: 'left',
  fontSize: 11,
  height: 25,
  overflow: 'hidden',
  lineHeight: 1.3,
  overflowY: 'scroll',
  color: theme.palette.grey[600],
  wordWrap: 'break-word',
  marginBottom: theme.spacing(1),
  paddingRight: theme.spacing(1),
}))

const CardStyled = styled(Card)(({ theme }) => ({
  width: 220,
  padding: theme.spacing(1),
  backgroundColor: theme.palette.background.default,
}))

const ImageListWrapper = styled(Box)(({ theme }) => ({
  margin: theme.spacing(1, 0),
  display: 'flex',
  flexWrap: 'wrap',
}))

const ImageWrapper = styled(Box)(({ theme }) => ({
  borderRadius: '5px',
  position: 'relative',
  margin: theme.spacing(0.625),
}))

const ListItemWithImage: React.FC<ListItemWithImageProps> = ({
  isImage = false,
  pathValue,
  ...props
}) => {
  return (
    <Box sx={{ height: 100 }} {...props}>
      {isImage ? (
        <CardMedia image={pathValue} component='img' alt='' height='100' sx={{ borderRadius: 2 }} />
      ) : (
        <Box>
          <InsertDriveFileIcon />
        </Box>
      )}
    </Box>
  )
}

const CardItemLink: React.FC<
  ListItemProps & LinkProps & ListItemTextProps & ListItemWithImageProps
> = ({ isImage, pathValue, primary, ...rest }) => {
  return (
    <ImageWrapper>
      <ListItem {...rest} component={Link} sx={{ px: 0 }}>
        <CardStyled>
          <CardContent sx={{ p: 1 }}>
            <ImageCaption>{primary}</ImageCaption>
          </CardContent>
          <ListItemWithImage isImage={isImage} pathValue={pathValue} />
        </CardStyled>
      </ListItem>
    </ImageWrapper>
  )
}

const defaultFieldProps = { title: 'name', targetSourcePath: 'full_path', src: null }
const generateFileItemProps: GenerateFileItemPropsFn = (file, fieldProps = defaultFieldProps) => {
  const { title, targetSourcePath } = { ...defaultFieldProps, ...fieldProps }
  const fileTitleValue = get(file, title) || title
  // checking the file is image or not?
  const mimeType = get(file, 'rawFile.type') || get(file, 'mimetype')
  const isImage = mimeType.search('image') >= 0
  // get filesize
  const fileSize = formatBytes(get(file, 'rawFile.size') || get(file, 'size'))
  // setting path value
  const srcValue = get(file, targetSourcePath) || get(file, defaultFieldProps.targetSourcePath)
  const srcIsBlob = isBlob(srcValue)
  const srcUrl = imageUrlConcat(srcValue)
  const pathValue = srcIsBlob ? srcValue : srcUrl

  return {
    fileTitleValue,
    mimeType,
    fileSize,
    isImage,
    srcValue,
    srcIsBlob,
    srcUrl,
    pathValue,
  }
}

export const MultipleImageField: React.FC<FileFieldProps> = (props) => {
  const { className, emptyText, source, title, target, src, download, ping, rel, ...rest } = props
  const [mainSource, targetSourcePath] = source.split('.')
  const record = useRecordContext(props)
  const sourceValue = get(record, targetSourcePath || mainSource)

  // if the field doesn't exist.
  if (!sourceValue || isEmpty(sourceValue)) {
    return emptyText ? (
      <Typography component='span' variant='body2' className={className}>
        {emptyText}
      </Typography>
    ) : (
      <Root className={className} {...sanitizeFieldRestProps(rest)} />
    )
  }

  // if the field is array file object
  if (isArray(sourceValue)) {
    return (
      <ImageListWrapper>
        {sourceValue.map((file) => {
          const { fileTitleValue, fileSize, isImage, pathValue } = generateFileItemProps(file, {
            title,
            targetSourcePath: targetSourcePath || mainSource,
          })
          return (
            <Box key={fileTitleValue}>
              <CardItemLink
                href={pathValue}
                title={fileTitleValue}
                target={target}
                download={download}
                ping={ping}
                rel={rel}
                primary={fileTitleValue}
                secondary={fileSize}
                isImage={isImage}
                pathValue={pathValue}
              />
            </Box>
          )
        })}
      </ImageListWrapper>
    )
  }

  const { fileTitleValue, fileSize, isImage, pathValue } = generateFileItemProps(record, {
    title,
    targetSourcePath: src || targetSourcePath || mainSource,
  })

  return (
    <CardItemLink
      href={pathValue}
      title={fileTitleValue}
      target={target}
      download={download}
      ping={ping}
      rel={rel}
      primary={fileTitleValue}
      secondary={fileSize}
      isImage={isImage}
      pathValue={pathValue}
    />
  )
}
